import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

const BG = "#131a34";
const cyan = "#10b2f2";
const white = "#ffffff";
const cardBg = "rgba(255,255,255,0.04)";
const cardBorder = "rgba(255,255,255,0.08)";

const STATS = [
  {
    value: 18,
    suffix: "M+",
    label: "Americans",
    sub: "were victims of identity fraud in 2024",
  },
  {
    value: 47,
    suffix: "B+",
    label: "dollars lost",
    sub: "to identity fraud and scams in 2024",
  },
  {
    value: 3200,
    suffix: "+",
    label: "data breaches",
    sub: "recorded in the US in 2023 alone",
  },
];

function useCounter(target, duration = 1600, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, started]);
  return count;
}

const StatCard = ({ stat, started }) => {
  const count = useCounter(stat.value, 1600, started);

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: "16px",
        p: { xs: 3.5, md: 5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        transition: "border-color 0.25s, transform 0.25s",
        "&:hover": {
          borderColor: `${cyan}55`,
          transform: "translateY(-3px)",
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: 52, md: 68 },
          lineHeight: 1,
          color: cyan,
          letterSpacing: "-2px",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count.toLocaleString()}
        {stat.suffix}
      </Typography>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 13,
          color: white,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        {stat.label}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: white,
          lineHeight: 1.6,
        }}
      >
        {stat.sub}
      </Typography>
    </Box>
  );
};

function OrbitDivider() {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        my: { xs: 4, md: 6 },
      }}
    >
      <style>{`
        @keyframes orbit-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes orbit-spin-reverse {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes orbit-pulse {
          0%, 100% { opacity: 0.12; }
          50%       { opacity: 0.32; }
        }
        .od-outer {
          transform-origin: 200px 60px;
          animation: orbit-spin 22s linear infinite;
        }
        .od-middle {
          transform-origin: 200px 60px;
          animation: orbit-spin-reverse 14s linear infinite;
        }
        .od-inner {
          transform-origin: 200px 60px;
          animation: orbit-spin 9s linear infinite;
        }
        .od-pulse {
          animation: orbit-pulse 3.5s ease-in-out infinite;
        }
      `}</style>
      <svg
        width="400"
        height="120"
        viewBox="0 0 400 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ellipse
          cx="200"
          cy="60"
          rx="185"
          ry="52"
          stroke="rgba(16,178,242,0.08)"
          strokeWidth="1"
          className="od-pulse"
        />
        <g className="od-outer">
          <ellipse
            cx="200"
            cy="60"
            rx="172"
            ry="46"
            stroke="rgba(16,178,242,0.22)"
            strokeWidth="1.5"
            strokeDasharray="7 11"
            strokeLinecap="round"
          />
          <circle cx="200" cy="14" r="4" fill="#f59e0b" opacity="0.8" />
        </g>
        <g className="od-middle">
          <ellipse
            cx="200"
            cy="60"
            rx="130"
            ry="34"
            stroke="rgba(16,178,242,0.14)"
            strokeWidth="1"
            strokeDasharray="5 14"
            strokeLinecap="round"
          />
          <circle cx="200" cy="26" r="3" fill="#f59e0b" opacity="0.55" />
        </g>
        <g className="od-inner">
          <ellipse
            cx="200"
            cy="60"
            rx="82"
            ry="20"
            stroke="rgba(16,178,242,0.1)"
            strokeWidth="1"
            strokeDasharray="3 16"
            strokeLinecap="round"
          />
          <circle cx="200" cy="40" r="2.5" fill="#f59e0b" opacity="0.4" />
        </g>
        <circle cx="28" cy="60" r="3" fill="#f59e0b" opacity="0.4" />
        <circle cx="372" cy="60" r="3" fill="#f59e0b" opacity="0.4" />
        <circle cx="200" cy="106" r="3" fill="#f59e0b" opacity="0.25" />
      </svg>
    </Box>
  );
}

const DataStatsSection = () => {
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.15 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        bgcolor: BG,
        width: "100%",
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 0 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: 28, sm: 36, md: 44 },
            color: white,
            lineHeight: 1.2,
            letterSpacing: "-0.5px",
          }}
        >
          Your data is out there.{" "}
          <Box component="span" sx={{ color: cyan }}>
            Right now.
          </Box>
        </Typography>

        <Typography
          sx={{
            mt: 2,
            fontSize: { xs: 15, md: 16 },
            maxWidth: 480,
            mx: "auto",
            lineHeight: 1.65,
            color: white,
          }}
        >
          The personal data industry operates around the clock. Here's what the
          numbers say about how exposed you really are.
        </Typography>
      </Box>

      <OrbitDivider />

      {/* Cards */}
      <Box
        sx={{
          maxWidth: 1000,
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 3 },
        }}
      >
        {STATS.map((stat, i) => (
          <StatCard key={i} stat={stat} started={started} />
        ))}
      </Box>

      <Typography
        sx={{
          mt: 5,
          textAlign: "center",
          fontSize: 11,
          color: "rgba(255,255,255,0.2)",
          maxWidth: 560,
          mx: "auto",
        }}
      >
        Sources: Javelin Strategy & Research / AARP (2025), Identity Theft
        Resource Center (2023)
      </Typography>
    </Box>
  );
};

export default DataStatsSection;
